/**
 * overseerClicks.ts — m31 P2 (O6): clean click capture for the app shell, installed by <OverseerBridge/> once the SDK runs.
 *
 * The SDK reports every click with a raw CSS selector (`button.px-4`) that the proxy drops. This reports the ones that
 * matter under a name WE chose: a capture-phase `click` listener finds the nearest interactive ancestor of the target
 * (`a, button, [role=button|tab|menuitem], input, select, textarea, label, summary, [data-ov]`) and labels it, in order, by
 *   1. its `data-ov`   — a `<surface>.<action>` label (the closed list, OV_LABELS, is enforced at build time by T4)   checkout.place_order
 *   2. its `id`        — only if it reads like a human-chosen name (no digit runs, no generated ids)  id:card-number
 *   3. its tag, `:role` when it has a plain one                                                       button, div:button
 *      — and for a link, where it goes: an internal path with every id-looking segment replaced by `_`
 *        (a:p.dashboard.project._.manage), or `external` / `mailto` / `tel` / `sms` / `hash` / `js`.
 * and reports `click {selector: 'sf:<label>'}` through ovTrackClick — the only door to the SDK. The label alphabet is the
 * proxy's (letters, digits, `_ . : -`; see OV_SELECTOR_RE): that is why the spec's `#id` and `tag[role]` read `id:` and
 * `tag:role` here — the proxy drops a selector outside its alphabet. NEVER the element's text, its value, its title /
 * aria-label or an href's query: privacy §2.3 promises "a label we assign (never the text you type)".
 *
 * Frustration signals use the same labels (and the SDK's own numbers):
 *   rage  = 3 clicks on the same label inside 1 s, back to back          → `rage_click`
 *   dead  = no DOM change and no URL change 700 ms after the click      → `dead_click`
 * The dead-click check is stricter than the SDK's in the places where "nothing changed" is not evidence of a problem: a
 * form field or label (focusing or ticking it mutates no attribute), and a link the browser itself followed or opened
 * (new tab, download, mailto:, a full page load). Those would otherwise fire a dead click on every ordinary use.
 *
 * Pure of globals: installClickCapture takes the document, a sink and the timer functions, so jest drives it with fakes;
 * installOverseerClicks is the production wiring. Free of `import.meta` (jest loads it).
 */
import { ovTrackClick, type OvClickKind } from '../services/overseer';
import { hasOvLabelShape } from '../services/overseerLabels';

export const RAGE_CLICKS = 3;
export const RAGE_WINDOW_MS = 1000;
export const DEAD_CLICK_MS = 700;
/** The browser re-dispatches a click on a <label>'s control right after the label's own; that echo is not a second click. */
const LABEL_ECHO_MS = 150;

// ── A DOM surface small enough for a plain-object fake ──────────────────────────────────────────────────────────────

export interface ElementLike {
  tagName?: string;
  id?: string;
  parentElement?: ElementLike | null;
  getAttribute?(name: string): string | null;
  hasAttribute?(name: string): boolean;
  /** HTMLLabelElement.control */
  control?: ElementLike | null;
  querySelector?(selector: string): ElementLike | null;
}

function tagOf(el: ElementLike | null | undefined): string {
  try {
    return typeof el?.tagName === 'string' ? el.tagName.toUpperCase() : '';
  } catch {
    return '';
  }
}

function attr(el: ElementLike | null | undefined, name: string): string | null {
  try {
    const v = el && typeof el.getAttribute === 'function' ? el.getAttribute(name) : null;
    return typeof v === 'string' ? v : null;
  } catch {
    return null;
  }
}

function hasAttr(el: ElementLike | null | undefined, name: string): boolean {
  try {
    if (!el) return false;
    if (typeof el.hasAttribute === 'function') return el.hasAttribute(name);
    return attr(el, name) !== null;
  } catch {
    return false;
  }
}

// ── Which element a click is "on" ───────────────────────────────────────────────────────────────────────────────────

const INTERACTIVE_TAGS: ReadonlySet<string> = new Set(['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'LABEL', 'SUMMARY']);
const INTERACTIVE_ROLES: ReadonlySet<string> = new Set(['button', 'tab', 'menuitem']);
const MAX_CLIMB = 60;

export function isInteractive(el: ElementLike | null | undefined): boolean {
  if (!el) return false;
  if (INTERACTIVE_TAGS.has(tagOf(el))) return true;
  if (INTERACTIVE_ROLES.has((attr(el, 'role') || '').toLowerCase())) return true;
  return hasAttr(el, 'data-ov');
}

/** The element itself or its closest ancestor that is interactive; null when the click landed on plain content. */
export function nearestInteractive(start: ElementLike | null | undefined): ElementLike | null {
  let el: ElementLike | null | undefined = start;
  for (let i = 0; el && i < MAX_CLIMB; i++) {
    if (isInteractive(el)) return el;
    el = el.parentElement;
  }
  return null;
}

// ── Clean labels ────────────────────────────────────────────────────────────────────────────────────────────────────

const ID_RE = /^[A-Za-z][A-Za-z0-9_-]{0,39}$/;
const ROLE_RE = /^[a-z]{3,20}$/;
const TAG_RE = /^[a-z][a-z0-9-]{0,29}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** A link's path keeps at most this many segments, each at most this long (3 + 2 + 4 × 24 = 101 characters at most) — well inside the proxy's 116-character cap. */
const MAX_PATH_SEGMENTS = 4;
const MAX_SEGMENT_LENGTH = 23; // 24+ characters is "not a name" to looksGenerated as well

/**
 * Reads like a generated or user-derived value rather than a name someone chose: a uuid, a long number, a hash, a token
 * (`tok_AbC123xyz…`: a word that mixes letters and digits), or anything long enough that it is not a name. A false alarm
 * only costs a less specific label (`:id`, or the tag), so this errs towards "generated".
 */
function looksGenerated(s: string): boolean {
  if (UUID_RE.test(s) || /\d{4,}/.test(s)) return true;
  if (/[0-9a-f]{8,}/i.test(s) && /\d/.test(s)) return true;
  if (s.length >= 24) return true;
  return s.split(/[-_]/).some((word) => word.length >= 8 && /\d/.test(word) && /[a-z]/i.test(word));
}

function safeId(id: unknown): string | null {
  return typeof id === 'string' && ID_RE.test(id) && !looksGenerated(id) ? id : null;
}

/** One path segment of a link, or `_` when it is not a plain route word: an id, a token, a file name, anything odd or long. */
function templateSegment(seg: string): string {
  if (!/^[A-Za-z0-9_-]+$/.test(seg) || seg.length > MAX_SEGMENT_LENGTH) return '_';
  if (/^\d+$/.test(seg) || looksGenerated(seg) || /^[0-9a-f]{8,}$/i.test(seg)) return '_';
  return seg.toLowerCase();
}

/**
 * Where a link goes, as a word that cannot carry a person's data: `hash` (same page), `mailto` / `tel` / `sms`, `js`,
 * `external` (another site, or any other scheme), or `p` + the internal path's segments, `.`-separated, with the query and
 * hash dropped and every id-looking segment replaced by `_` (`/dashboard/project/<uuid>/manage` → `p.dashboard.project._.manage`,
 * the home page → `p`). `origin` decides what is "internal"; empty or unusable → null.
 */
export function templateHref(href: string | null | undefined, origin = ''): string | null {
  const h = typeof href === 'string' ? href.trim() : '';
  if (!h) return null;
  if (h.startsWith('#')) return 'hash';
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(h);
  if (scheme) {
    const s = scheme[1].toLowerCase();
    if (s === 'mailto' || s === 'tel' || s === 'sms') return s;
    if (s === 'javascript') return 'js';
  }
  try {
    const base = origin || 'http://local.invalid';
    const url = new URL(h, base);
    if (url.origin !== new URL(base).origin || (url.protocol !== 'http:' && url.protocol !== 'https:')) return 'external';
    const segments = url.pathname.split('/').filter(Boolean).slice(0, MAX_PATH_SEGMENTS).map(templateSegment);
    return segments.length ? `p.${segments.join('.')}` : 'p';
  } catch {
    return 'external';
  }
}

/** The tag as a label word; anything that is not a plain lower-case tag name (a namespaced or odd custom element) is `unknown`. */
function safeTag(el: ElementLike): string {
  const tag = tagOf(el).toLowerCase();
  return TAG_RE.test(tag) ? tag : 'unknown';
}

/** The name a control is reported under (without the `sf:` prefix). See the header for the order; always inside the proxy's alphabet. */
export function cleanLabel(el: ElementLike, origin = ''): string {
  const ov = attr(el, 'data-ov');
  if (hasOvLabelShape(ov)) return ov;
  const id = safeId(el.id);
  if (id) return `id:${id}`;
  const tag = safeTag(el);
  if (tag === 'a') {
    const dest = templateHref(attr(el, 'href'), origin);
    if (dest) return `a:${dest}`;
  }
  const role = (attr(el, 'role') || '').toLowerCase();
  return ROLE_RE.test(role) ? `${tag}:${role}` : tag;
}

// ── Rage ────────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface RageDetector {
  /** Records a click on `key`; true when it is the 3rd in a row on the same key inside the window (and starts over). */
  record(key: string, now?: number): boolean;
}

/** The SDK's own rule: consecutive clicks on the same key; a different key starts over. */
export function createRageDetector(threshold = RAGE_CLICKS, windowMs = RAGE_WINDOW_MS): RageDetector {
  let hits: number[] = [];
  let lastKey: string | null = null;
  return {
    record(key, now = Date.now()) {
      if (key !== lastKey) {
        hits = [];
        lastKey = key;
      }
      hits = hits.filter((t) => now - t < windowMs);
      hits.push(now);
      if (hits.length >= threshold) {
        hits = [];
        return true;
      }
      return false;
    },
  };
}

// ── The listener ────────────────────────────────────────────────────────────────────────────────────────────────────

interface MutationObserverLike {
  observe(target: unknown, options: Record<string, unknown>): void;
  disconnect(): void;
}

export interface ClickEventLike {
  target?: unknown;
  isTrusted?: boolean;
  defaultPrevented?: boolean;
}

export interface ClickCaptureDeps {
  doc: {
    addEventListener(type: string, listener: (ev: ClickEventLike) => void, capture: boolean): void;
    removeEventListener(type: string, listener: (ev: ClickEventLike) => void, capture: boolean): void;
    documentElement?: unknown;
  };
  /** The one place a click leaves the module. Returns whether the event was handed to the SDK. */
  sink: (kind: OvClickKind, selector: string) => boolean;
  /** Current URL, compared before / after a click (default: window.location.href). */
  href?: () => string;
  /** What counts as "internal" for a link (default: window.location.origin). */
  origin?: () => string;
  now?: () => number;
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
  MutationObserver?: new (callback: () => void) => MutationObserverLike;
}

function safely(fn: () => void): void {
  try {
    fn();
  } catch {
    // analytics must never break a click
  }
}

function labelControl(label: ElementLike): ElementLike | null {
  try {
    return label.control || (typeof label.querySelector === 'function' ? label.querySelector('input,select,textarea,button') : null) || null;
  } catch {
    return null;
  }
}

/** A form field or label: ticking, typing in or focusing it changes no DOM attribute, so "nothing changed" proves nothing. */
function isFormControl(el: ElementLike): boolean {
  const tag = tagOf(el);
  return tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || tag === 'LABEL';
}

/** A link the browser itself acts on (opens, downloads, mails, loads): only a click the page cancelled is the page's to answer. */
function followedByBrowser(el: ElementLike, ev: ClickEventLike): boolean {
  if (tagOf(el) !== 'A' || ev.defaultPrevented === true) return false;
  const href = (attr(el, 'href') || '').trim();
  return href !== '' && !href.startsWith('#') && !/^javascript:/i.test(href);
}

/**
 * Installs the capture-phase listener on `deps.doc`; returns the function that removes it (and cancels pending checks).
 * Every handler is wrapped: nothing here can throw into the page, and nothing here prevents or stops an event.
 */
export function installClickCapture(deps: ClickCaptureDeps): () => void {
  const now = deps.now ?? Date.now;
  const href = deps.href ?? (() => String((globalThis as { location?: { href?: string } }).location?.href ?? ''));
  const origin = deps.origin ?? (() => String((globalThis as { location?: { origin?: string } }).location?.origin ?? ''));
  const setT = deps.setTimeout ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const clearT = deps.clearTimeout ?? ((h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>));
  const MO = deps.MutationObserver ?? (globalThis as { MutationObserver?: ClickCaptureDeps['MutationObserver'] }).MutationObserver;

  const rage = createRageDetector();
  const checks = new Map<{ mutated: boolean }, unknown>(); // dead-click checks in flight → their timer
  let observer: MutationObserverLike | null = null;
  let echo: { el: ElementLike; until: number } | null = null;
  let stopped = false;

  function observe(): void {
    if (observer || !MO) return;
    try {
      const o = new MO(() => { for (const check of checks.keys()) check.mutated = true; });
      o.observe(deps.doc.documentElement, { childList: true, subtree: true, attributes: true });
      observer = o;
    } catch {
      observer = null;
    }
  }

  function release(): void {
    if (checks.size === 0 && observer) {
      safely(() => (observer as MutationObserverLike).disconnect());
      observer = null;
    }
  }

  function watchForDeadClick(selector: string, el: ElementLike, ev: ClickEventLike): void {
    if (!MO || isFormControl(el)) return;
    const check = { mutated: false };
    const urlBefore = href();
    observe();
    checks.set(check, setT(() => safely(() => {
      checks.delete(check);
      release();
      if (stopped || check.mutated || href() !== urlBefore || followedByBrowser(el, ev)) return;
      deps.sink('dead_click', selector);
    }), DEAD_CLICK_MS));
  }

  const onClick = (ev: ClickEventLike): void => safely(() => {
    if (stopped || ev.isTrusted === false) return; // a script's .click() is not a person
    const raw = ev.target as (ElementLike & { nodeType?: number }) | null | undefined;
    const start = raw && raw.nodeType === 3 ? raw.parentElement : raw;
    const t = now();

    // The echo of a label click: the browser clicks the labelled control right after the label. Count the person once.
    if (echo) {
      const isEcho = start === echo.el && t <= echo.until;
      echo = null;
      if (isEcho) return;
    }

    const el = nearestInteractive(start);
    if (!el) return;

    let label: string;
    if (tagOf(el) === 'LABEL') {
      const control = labelControl(el);
      if (control) echo = { el: control, until: t + LABEL_ECHO_MS };
      label = hasOvLabelShape(attr(el, 'data-ov')) || !control ? cleanLabel(el, origin()) : cleanLabel(control, origin());
    } else {
      label = cleanLabel(el, origin());
    }
    const selector = `sf:${label}`;

    if (!deps.sink('click', selector)) return; // not recording (muted, SDK not running): no bookkeeping either
    if (rage.record(selector, t)) deps.sink('rage_click', selector);
    watchForDeadClick(selector, el, ev);
  });

  safely(() => deps.doc.addEventListener('click', onClick, true));

  return () => {
    stopped = true;
    safely(() => deps.doc.removeEventListener('click', onClick, true));
    for (const timer of checks.values()) safely(() => clearT(timer));
    checks.clear();
    release();
  };
}

// ── Production wiring ───────────────────────────────────────────────────────────────────────────────────────────────

let installed: (() => void) | null = null;

/**
 * Starts click capture on the page's document — once. Called by syncOverseer after the SDK is running; a second call is a
 * no-op that returns the same remover. Where there is no document (SSR, a test without a DOM) it does nothing.
 */
export function installOverseerClicks(): () => void {
  if (installed) return installed;
  try {
    const doc = typeof document !== 'undefined' ? document : null;
    if (!doc || typeof doc.addEventListener !== 'function') return () => {};
    const stop = installClickCapture({ doc: doc as unknown as ClickCaptureDeps['doc'], sink: ovTrackClick });
    installed = () => {
      stop();
      installed = null;
    };
    return installed;
  } catch {
    return () => {};
  }
}

/** Takes the page listener off again (a session that turned into staff / impersonation); a no-op when none is installed. */
export function removeOverseerClicks(): void {
  try {
    if (installed) installed();
  } catch {
    // fail-silent
  }
}
